import { useConfigStore } from '@/store/config';
import { usePairs } from '@/store/pairs';
import BigNumber from 'bignumber.js';
import { useMemo } from 'react';
import { useShallow } from 'zustand/shallow';

export function useGetLeverage(pairIndex: number, isCustom?: boolean) {
    const pairs = usePairs(useShallow((state) => state.data));

    const pairTrade = pairs?.find((item) => item.pairIndex === pairIndex);

    const groups = useConfigStore((state) => state.fees);

    const custom = isCustom ?? true;

    const initLeverage = useMemo(() => {
        const group = groups.find((item) => item.name === pairTrade?.groupName);

        if (!group || !pairTrade?.maxLeverage) {
            return 5;
        }

        const innit = new BigNumber(group?.minLevPosDai)
            .dividedBy(Math.pow(10, 18))
            .dividedBy(pairTrade?.maxLeverage)
            .toNumber();

        if (!custom) {
            return innit;
        }
        return innit > 5 ? innit : 5;
    }, [custom, groups, pairTrade?.groupName, pairTrade?.maxLeverage]);

    const maxLeverage = useMemo(() => {
        return pairTrade?.maxLeverage ?? 150;
    }, [pairTrade?.maxLeverage]);

    const minLeverage = useMemo(() => {
        return pairTrade?.minLeverage ?? 2;
    }, [pairTrade?.minLeverage]);

    const leverageMarks = useMemo(() => {
        if (maxLeverage === 1000) {
            return {
                [minLeverage]: minLeverage.toString(),
                200: '200',
                400: '400',
                600: '600',
                800: '800',
                [maxLeverage]: maxLeverage.toString(),
            };
        }

        if (maxLeverage === 50) {
            return {
                [minLeverage]: minLeverage.toString(),
                10: '10',
                20: '20',
                30: '30',
                40: '40',
                [maxLeverage]: maxLeverage.toString(),
            };
        }

        if (maxLeverage === 100) {
            return {
                [minLeverage]: minLeverage.toString(),
                20: '20',
                40: '40',
                60: '60',
                80: '80',
                [maxLeverage]: maxLeverage.toString(),
            };
        }

        if (maxLeverage === 5) {
            return {
                [minLeverage]: minLeverage.toString(),
                3: '3',
                4: '4',
                [maxLeverage]: maxLeverage.toString(),
            };
        }

        if (maxLeverage === 10) {
            return {
                [minLeverage]: minLeverage.toString(),
                4: '4',
                6: '6',
                8: '8',
                [maxLeverage]: maxLeverage.toString(),
            };
        }

        if (maxLeverage === 500) {
            return {
                [minLeverage]: minLeverage.toString(),
                100: '100',
                200: '200',
                300: '300',
                400: '400',
                [maxLeverage]: maxLeverage.toString(),
            };
        }

        if (maxLeverage === 250) {
            return {
                [minLeverage]: minLeverage.toString(),
                50: '50',
                100: '100',
                150: '150',
                200: '200',
                [maxLeverage]: maxLeverage.toString(),
            };
        }

        const leverageCheck: Record<number, string> = {
            [minLeverage]: minLeverage.toString(),
        };

        for (
            let i = Math.floor(maxLeverage / 6);
            i < maxLeverage;
            i += Math.floor(maxLeverage / 6)
        ) {
            leverageCheck[i] = i.toString();
        }
        if (!leverageCheck?.[maxLeverage]) {
            leverageCheck[maxLeverage] = maxLeverage.toString();
        }
        return leverageCheck;
    }, [maxLeverage, minLeverage]);

    const result = Object.values(leverageMarks)

    return {
        leverageMarks,
        leverageArray: result,
        maxLeverage,
        minLeverage,
        initLeverage,
    };
}
